import React, { useEffect, useState } from 'react';
import { mapApi } from '../../../requests/api';
import { ExternalIcon } from '../icons';

/**
 * The controls around the owner list: the escape hatch, the source tag every
 * name wears, and the Texas link. The list itself is OwnerList.
 */

export function NeedOwnerToggle({ value, onChange }) {
    return (
        <button
            type="button"
            className={'NeedOwner' + (value ? ' NeedOwner--on' : '')}
            onClick={() => onChange(!value)}
            aria-pressed={value}
        >
            <span className="NeedOwner-label">Need owner</span>
            <span className="NeedOwner-track"><span className="NeedOwner-knob" /></span>
        </button>
    );
}

/**
 * Where a name on this card came from.
 *
 * The card showed the assessor's owner of record under the heading "Investor",
 * styled exactly like an investor iCLIMB holds, and a property with no owner at
 * all read as though it had one — which is what the client reported against
 * 6860 Lebanon Rd.
 */
export function SourceTag({ source }) {
    const label = source === 'iclimb' ? 'iCLIMB' : 'LightBox';

    return <span className={'Source Source--' + source}>{label}</span>;
}

function lookupStatus(result) {
    if (result.url) {
        return 'found';
    }

    return result.ambiguous ? 'ambiguous' : 'missing';
}

/**
 * The entity's Texas franchise tax record.
 *
 * The name has to be searched before the Comptroller's own page can be linked
 * by taxpayer number, which is why this resolves itself rather than being a
 * plain href. It used to mount only when an owner row was opened, so most
 * parcels never paid for it — and the client could not find it. One lookup per
 * Texas card, answered from a 30-day server cache, buys it being visible.
 */
export function ComptrollerLink({ entityName, ownerAddress }) {
    const [state, setState] = useState({ status: 'loading', url: null, error: '' });

    // Looked up on mount, not behind a button that turns into the link you then
    // have to click again.
    useEffect(() => {
        let live = true;

        setState({ status: 'loading', url: null, error: '' });

        mapApi
            .comptroller(entityName, ownerAddress)
            .then((result) => live && setState({ status: lookupStatus(result), url: result.url, error: '' }))
            .catch((problem) => live && setState({ status: 'error', url: null, error: problem.message }));

        return () => {
            live = false;
        };
    }, [entityName, ownerAddress]);

    if (state.status === 'found') {
        return (
            <a className="Comptroller Comptroller--found" href={state.url} target="_blank" rel="noopener noreferrer">
                Texas Comptroller
                <ExternalIcon />
            </a>
        );
    }

    if (state.status === 'missing') {
        return <span className="Comptroller Comptroller--missing">No Texas franchise record for {entityName}</span>;
    }

    if (state.status === 'ambiguous') {
        return <span className="Comptroller Comptroller--missing">No clear Texas franchise record for {entityName}</span>;
    }

    if (state.status === 'error') {
        return <span className="Comptroller Comptroller--error">{state.error}</span>;
    }

    return <span className="Comptroller Comptroller--waiting">Looking up the Comptroller…</span>;
}
